import conf from '@configuration'
import { Button} from '@components/common'
import {Duration} from 'luxon'
import Play from '../../../images/icons/play_svg.svg'
import React, {useState} from 'react'
import {Product, ProductItem, ProductType} from '../../../types/rest'
import useMediaQuery from '../../../hooks/useMediaQuery'
import {small} from '../../../constants'
import {useSelector} from 'react-redux'
import {getLanguage, getTranslation, getUser} from '@store/selectors'
import styled from 'styled-components'
import {useLocation, useNavigate} from 'react-router-dom'
import notFoundImg from '../../../images/icons/kh-notFound.svg'
import {getProductPrice} from '../../../helpers/getProductPrice'

type Props = {
  product: Product
  selectedItem?: { keep: boolean; item: ProductItem }
  setPlayTrailer: React.Dispatch<React.SetStateAction<boolean>>
  setPlayContent: ()=> void
}

// Componente con thumbnail prodotto e bottone per continuare le lezioni se il prodotto è comprato e l'utente
// ha iniziato a vedere qualcosa, altrimenti fa partire dalla prima, oppure se non è acquistato propone
// l'acquisto
const CourseShowPreview: React.FC<Props> = ({product, selectedItem, setPlayTrailer, setPlayContent}) => {
  const language = useSelector(getLanguage)
  const translation = useSelector(getTranslation)
  const isMobile = useMediaQuery(`(max-width: ${small})`)
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector(getUser)
  const [notFound, setNotFound]= useState(false)

  const getItemVideoFormat = (item: any) => {
    const videoUrl = item?.item?.video?.url
    if(videoUrl) {
      return videoUrl.slice(videoUrl.lastIndexOf('.'))
    }
    return ''
  }
  const getPurchasedItemButtonLabel = (item: any) => {
    const videoFormat = getItemVideoFormat(item)
    if(item.keep) {
      return translation.common?.buttons.keepWatch
    } else {
      if(videoFormat === '.mp3') return translation.common?.buttons.listen
      return translation.common?.buttons.watch
    }
  }


  if (Object.keys(product).length === 0 || !selectedItem) return null

  return (
    <VideoContainer notFound={notFound} isMobile={isMobile}>
      <Jumbo
        src={!notFound ? `${conf.baseUrl}${selectedItem?.item?.thumbnail ? selectedItem?.item?.thumbnail : product.thumbnail}`: notFoundImg}
        style={{backgroundColor: notFound ? '#000000' : 'transparent'}}
        onError={({currentTarget}) => {
          currentTarget.onerror = null // prevents looping
          setNotFound(true)
        }}
      />
      <Layover>
        <JumboContent>
          <h2 style={{textAlign: 'center', fontSize: isMobile ? '2rem' : '4rem', margin: '1rem 0'}}>
            {product.purchased || ((product.type as ProductType).label === 'masterclass' && user && user.verified) ? (selectedItem?.item?.translations[language].title || product.translations[language].title) : product.translations[language].title}
          </h2>
          {product.purchased || ((product.type as ProductType).label === 'masterclass' && user && user.verified) ? (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '3rem 0'}}>
              <Button
                onClick={() => setPlayContent()}
                buttonType={'secondary-inverted'}
                //text={ selectedItem?.keep ? translation.common?.buttons.keepWatch : translation.common?.buttons.watch}/>
                text={(product.type as ProductType).label === 'website' ? translation.common.buttons.visitWebsite : getPurchasedItemButtonLabel(selectedItem)}/>

              {(product?.keepWatching && (
                <ModuleLessonNumber isMobile={isMobile}>
                  {selectedItem?.item?.time
                    ? `${Duration.fromObject({
                      seconds: selectedItem?.item?.time
                    }).toFormat('hh:mm:ss')}`
                    : ''}
                </ModuleLessonNumber>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '3rem',
                marginTop: '2rem'
              }}>
              <Button
                buttonType={'secondary-inverted'}
                onClick={() => {
                  let pathToGo = `/buy/${product.id}`
                  if (((product.type as ProductType).label !== 'course') || getProductPrice(product, user)?.amount === 0) pathToGo = `/login?redirectTo=${encodeURIComponent(location.pathname)}`
                  return navigate(pathToGo)
                }}
                text={
                  <div>
                    {((product.type as ProductType).label !== 'course' && (product.type as ProductType).label !== 'website') || getProductPrice(product, user)?.amount === 0 ?
                      (
                        <span style={{textAlign: 'center', width: '100%'}}>{translation.PAGES.login.login}</span>
                      ) : (
                        <span style={{display: 'flex', justifyContent: 'space-between'}}>
                          {translation.common?.buttons.buy}&nbsp;
                          <span>
                            {getProductPrice(product, user)?.amount}
                            {getProductPrice(product, user)?.currencySymbol}
                          </span>
                        </span>
                      )}
                  </div>
                }/>
              {product?.trailer && (
                <SoundContainer
                  onClick={() => {
                    setPlayTrailer(true)
                  }}>
                  <img style={{height: '1.6rem', marginRight: '1rem'}} src={Play} alt=""/>
                  <div style={{fontSize: '1.6rem', marginBottom: '2rem'}}>{translation.common?.buttons.trailer}</div>
                </SoundContainer>
              )}
            </div>
          )}
        </JumboContent>
      </Layover>
    </VideoContainer>
  )
}

export default CourseShowPreview

//region Style

const VideoContainer = styled.div<{ isMobile: boolean, notFound?: boolean}>`
  height: ${(props) => (props.isMobile ? '27rem' : '43rem')};

  display: flex;
  justify-content: center;
  align-items: center;
  max-width: var(--max-content-width);
  margin: 0 auto;
  ${({notFound})=> notFound ? 'background-color: #000000;' : ''}
  ${({notFound})=> notFound ? 'padding: 3rem;' : ''}

  & > img {
    flex-shrink: 0;
    width: auto;
    height: 100%;
    max-width: ${({isMobile})=> isMobile ? 'unset': '100%'};
    max-height: 100%;

  }
`

const Jumbo = styled.img`
  width: 100%;
  object-fit: contain;
`

const Layover = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, 0);
  margin:0 auto;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;

`
const JumboContent = styled.div`
  width: 90%;
  max-width: var(--max-content-width);
  margin: auto;
`

const ModuleLessonNumber = styled.h4<{ isMobile: boolean }>`
  text-align: center;
  margin-top: 3rem;
  font-size: ${(props) => (props.isMobile ? '1rem' : '1.8rem')};
  font-weight: 300;
`

const SoundContainer = styled.div`
  height: 3rem;
  display: flex;
  position: relative;
  top: 2rem;
  color: var(--text-over-color);
  cursor: pointer;
`

//endregion
